<template>
  <f7-page>
    <f7-navbar :title="$t('tontine.ledger')" back-link />
    <f7-toolbar tabbar bottom>
      <f7-link
        tab-link="tab-cycles"
        tab-link-active
        @click="activeTab = 'cycles'"
        >{{ $t("tontine.ledgerTabs.cycles") }}</f7-link
      >
      <f7-link tab-link="tab-payouts" @click="activeTab = 'payouts'">{{
        $t("tontine.ledgerTabs.payouts")
      }}</f7-link>
    </f7-toolbar>

    <f7-block v-if="loading" strong inset>
      <f7-preloader /> {{ $t("tontine.loading") }}
    </f7-block>

    <!-- Cycles tab -->
    <f7-tabs v-else>
      <f7-tab id="tab-cycles" tab-active-route tab-link-active>
        <AppLayout>
          <template #main>
            <f7-block strong inset class="ledger-note">
              <p class="text-color-gray">
                {{ $t("tontine.ledgerCyclesNote") }}
              </p>
            </f7-block>
            <f7-list v-if="cycles.length" inset class="ledger-list">
              <f7-list-item
                v-for="cycle in cycles"
                :key="cycle.id"
                :title="
                  $t('tontine.ledgerCycleNumber', { number: cycle.cycleNumber })
                "
                :subtitle="
                  cycle.beneficiaryDisplayNameSnapshot ||
                  $t('tontine.beneficiary')
                "
                :after="`${money(cycle.expectedTotal)} · ${payoutStatusLabel(
                  cycle.payoutStatus,
                )}`"
                @click="openCycle(cycle)"
              />
            </f7-list>
            <f7-block v-else class="empty-state">
              <f7-icon f7="square_grid_2x2" size="40" color="gray" />
              <p>{{ $t("tontine.noCycles") }}</p>
            </f7-block>
          </template>
        </AppLayout>
      </f7-tab>

      <!-- Payouts tab -->
      <f7-tab id="tab-payouts">
        <AppLayout>
          <template #main>
            <f7-block strong inset class="ledger-note">
              <p class="text-color-gray">
                {{ $t("tontine.ledgerPayoutsNote") }}
              </p>
            </f7-block>
            <f7-list v-if="payouts.length" inset class="ledger-list">
              <f7-list-item
                v-for="payout in payouts"
                :key="payout.id"
                :title="money(payout.amount, payout.currency)"
                :subtitle="
                  payout.beneficiaryDisplayName || $t('tontine.beneficiary')
                "
                :after="payoutStatusLabel(payout.status)"
                @click="openPayout(payout)"
              />
            </f7-list>
            <f7-block v-else class="empty-state">
              <f7-icon f7="creditcard" size="40" color="gray" />
              <p>{{ $t("tontine.noPayouts") }}</p>
            </f7-block>
          </template>
        </AppLayout>
      </f7-tab>
    </f7-tabs>
  </f7-page>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { f7 } from "framework7-vue";
import { useI18n } from "vue-i18n";
import { useTontineStore } from "@/js/pinia-stores/tontine";
import { useKeycloakStore } from "@/js/pinia-stores/keycloak";
import AppLayout from "../../components/common/AppLayout.vue";

const { t } = useI18n();
const props = defineProps({ campaignId: String, groupId: String });
const store = useTontineStore();
const keycloak = useKeycloakStore();

const loading = computed(() => store.loading);
const cycles = computed(() => store.cycles || []);
const payouts = computed(() => store.payouts || []);
const activeTab = ref("cycles");

const payoutStatusLabel = (s) =>
  ({
    NOT_READY: t("tontine.payoutStatus.NOT_READY"),
    DRAFT: t("tontine.payoutStatus.DRAFT"),
    AWAITING_APPROVAL: t("tontine.payoutStatus.AWAITING_APPROVAL"),
    APPROVED: t("tontine.payoutStatus.APPROVED"),
    SUBMITTED_TO_PARTNER: t("tontine.payoutStatus.SUBMITTED_TO_PARTNER"),
    EXECUTED: t("tontine.payoutStatus.EXECUTED"),
    FAILED: t("tontine.payoutStatus.FAILED"),
    CANCELLED: t("tontine.payoutStatus.CANCELLED"),
  }[s] ||
  s ||
  "—");

onMounted(load);
async function load() {
  try {
    await Promise.all([
      store.fetchCycles(props.campaignId),
      store.fetchPayouts(props.campaignId),
    ]);
  } catch (err) {
    f7.dialog.alert(
      err.response?.data?.message || err.message || t("tontine.loadError"),
    );
  }
}

function openCycle(cycle) {
  f7.views.main.router.navigate(
    `/groups/${props.groupId}/campaigns/${props.campaignId}/tontine/cycles/${cycle.id}`,{props: { campaignId: props.campaignId, groupId: props.groupId, cycleId: cycle.id }}
  );
}
function openPayout(payout) {
  f7.views.main.router.navigate(
    `/groups/${props.groupId}/campaigns/${props.campaignId}/tontine/payouts/${payout.id}`,{props: { campaignId: props.campaignId, groupId: props.groupId, cycleId: payout.cycleId }}
  );
}

function money(v, c) {
  if (v == null) return "—";
  return new Intl.NumberFormat(keycloak.locale || "fr", {
    style: "currency",
    currency: c || "XOF",
    maximumFractionDigits: 0,
  }).format(v || 0);
}
</script>

<style scoped>
.ledger-note p {
  font-size: 12px;
  margin: 0;
}
.ledger-list {
  margin-bottom: 12px;
}
.empty-state {
  text-align: center;
  padding: 40px 16px;
}
.empty-state p {
  color: var(--f7-on-surface-variant);
  font-size: 14px;
}
</style>
